import { useState } from "react";
import { useQuery, useMutation, useIsMutating } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faFlag,
  faSearch,
  faWarning,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import { isEmpty } from "lodash";
import styled from "styled-components";
import JSONPretty from "react-json-pretty";
import { withAppHOC } from "../../hoc";
import {
  StyledContentContainer,
  StyledActionItemButtons,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledStatusPill,
  StyledModalContainer,
  StyledIconedText,
  StyledFormContainer,
} from "../../components/styled";
import PageHeader from "../../components/PageHeader/PageHeader";
import {
  MessageBox,
  Dropdown,
  TextInput,
  CustomConfirmButton,
  CustomButton,
  Fieldset,
  Label,
  Form,
  Button,
} from "../../components/FormElements";
import Modal from "../../components/Modal";
import Accordination from "../../components/Accordination/Accordination";
import { Grid, GridItem } from "../../components/Grid";
import Loader from "../../components/Loader/Loader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import { TableBuilder } from "../../components/TableElements";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { formatCurrency, getSearchParams } from "../../utils";
import FiltersButton from "../../components/FiltersButton/FiltersButton";
import { Image } from "../../components/Image";

const StyledBodyContainer = styled.section`
  font-size: 14px;
  font-weight: normal;
  color: #000;
  word-break: break-all;
  padding: 0px;
  line-height: 20px;
  border: 1px solid #e5e5e5;
  margin: 20px;
  border-radius: 5px;
`;

const StyledHeading = styled.h2`
  font-size: 16px;
  font-weight: normal;
  color: #000;
  background-color: #e5e5e5;
  padding: 15px;
  margin: 0px;
`;

const StyledContent = styled.section`
  padding: 20px;
  @media (max-width: 600px) {
    word-break: break-all;
    overflow-x: scroll;
  }
`;

const StyledApiCallLogsContainer = styled.section`
  width: auto;
  height: auto;
  max-height: 500px;
  overflow-y: scroll;
  padding: 20px;
  pre {
    white-space: pre-wrap;
  }
`;

const BuyerTransactionsList = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    fOperatorsId: "",
    fCirclesId: "",
    fCategoriesId: "",
    fRechargeStatus: "",
    fRechargeNumber: "",
    fFromDate: "",
    fToDate: "",
    fApiSettingsId: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [isLogsLoading, setIsLogsLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [logs, setLogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [item, setItem] = useState(null);
  const [reason, setReason] = useState("");
  const [remark, setRemark] = useState("");
  const [rightAccountNumber, setRightAccountNumber] = useState(null);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 30,
  });
  const [apiState, setApiState] = useState({
    apiStatus: "",
    apiMessage: "",
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const readAPISettings = async () => {
    try {
      const response = await API.get(`/buyer/api-settings`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: apiSettings } = useQuery({
    queryKey: ["buyer_api_settings"],
    queryFn: readAPISettings,
  });

  const readData = async () => {
    try {
      const response = await API.get(
        `/buyer/transactions?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fOperatorsId=${appliedFilters.fOperatorsId}&fCirclesId=${appliedFilters.fCirclesId}&fCategoriesId=${appliedFilters.fCategoriesId}&fRechargeStatus=${appliedFilters.fRechargeStatus}&fRechargeNumber=${appliedFilters.fRechargeNumber}&fFromDate=${appliedFilters.fFromDate}&fToDate=${appliedFilters.fToDate}&fApiSettingsId=${appliedFilters.fApiSettingsId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const exportData = async () => {
    try {
      const response = await API.get(
        `/buyer/transactions-export?fOperatorsId=${appliedFilters.fOperatorsId}&fCirclesId=${appliedFilters.fCirclesId}&fCategoriesId=${appliedFilters.fCategoriesId}&fRechargeStatus=${appliedFilters.fRechargeStatus}&fRechargeNumber=${appliedFilters.fRechargeNumber}&fFromDate=${appliedFilters.fFromDate}&fToDate=${appliedFilters.fToDate}&fApiSettingsId=${appliedFilters.fApiSettingsId}`
      );
      setApiState({
        apiStatus: response?.data?.status ?? "",
        apiMessage: response?.data?.message ?? "",
      });
      setTimeout(() => {
        setApiState({
          apiStatus: "",
          apiMessage: "",
        });
      }, 3000);
    } catch (error) {
      throw new Error(error);
    }
  };

  const raiseComplaint = async ({ id, complaintBuyerRemark }) => {
    try {
      const response = await API.post(`/buyer/transactions/complaint`, {
        id,
        remark: complaintBuyerRemark,
      });
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const readApiLogs = async (id) => {
    setIsLogsLoading(true);
    try {
      const response = await API.get(`/buyer/transactions/logs/${id}`);
      if (response.data.status === RESPONSE_STATUSES.SUCCESS) {
        setLogs(response?.data?.data);
      }
    } catch (error) {
      throw new Error(error);
    } finally {
      setIsLogsLoading(false);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["transactions", paginationData, appliedFilters],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: meta } = useQuery({
    queryKey: ["meta"],
    queryFn: readMeta,
  });

  const { mutateAsync: raiseComplaintMutation } = useMutation({
    mutationFn: raiseComplaint,
    onError: (error) => console.error(error),
    onSuccess: () => {
      reloadData();
    },
  });

  const isMutation = useIsMutating();

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      fOperatorsId: "",
      fCirclesId: "",
      fCategoriesId: "",
      fRechargeStatus: "",
      fRechargeNumber: "",
      fFromDate: "",
      fToDate: "",
      fApiSettingsId: "",
    });
    setAppliedFilters({
      fOperatorsId: "",
      fCirclesId: "",
      fCategoriesId: "",
      fRechargeStatus: "",
      fRechargeNumber: "",
      fFromDate: "",
      fToDate: "",
      fApiSettingsId: "",
    });
  };

  const toggleApiLogs = async (id) => {
    setModalType("API_LOGS");
    setShowModal(true);
    await readApiLogs(id);
  };

  const raiseWrongToRight = async (e) => {
    e.preventDefault();
    setIsLoadingUpdate(true);
    setResponseStatus("");
    setResponseMessage("");
    API.post(`/buyer/wrong-to-right-transactions`, {
      id: item.id,
      reason,
      remark,
      rightAccountNumber,
    })
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === RESPONSE_STATUSES.SUCCESS) {
          reloadData();
          setTimeout(() => {
            setShowModal(false);
            setItem(null);
            setReason("");
            setRemark("");
            setRightAccountNumber(null);
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(RESPONSE_STATUSES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoadingUpdate(false);
      });
  };

  const renderPageHeading = () => (
    <PageHeader
      title="Transactions"
      showAddButton={false}
      showReloadData={true}
      reloadData={reloadData}
      showExportButton={true}
      onClickExport={exportData}
    />
  );

  const operatorsOptions = [
    {
      name: "OPERATOR",
      value: "",
    },
  ];
  !isEmpty(meta?.operators) &&
    meta?.operators?.forEach(({ operatorName, id }) =>
      operatorsOptions?.push({ name: operatorName, value: id })
    );

  const circlesOptions = [
    {
      name: "CIRCLE",
      value: "",
    },
  ];
  !isEmpty(meta?.circles) &&
    meta?.circles?.forEach(({ circleName, id }) =>
      circlesOptions?.push({ name: circleName, value: id })
    );

  const categoriesOptions = [
    {
      name: "CATEGORY",
      value: "",
    },
  ];
  !isEmpty(meta?.categories) &&
    meta?.categories?.forEach(({ category, id }) =>
      categoriesOptions?.push({ name: category, value: id })
    );

  const rechargeStatusOptions = [
    {
      name: "STATUS",
      value: "",
    },
    {
      name: "Pending",
      value: "Pending",
    },
    {
      name: "Success",
      value: "Success",
    },
    {
      name: "Failed",
      value: "Failed",
    },
  ];

  const apiSettingsOptions = [
    {
      name: "API SETTING",
      value: "",
    },
  ];

  if (apiSettings && Array.isArray(apiSettings) && apiSettings.length > 0) {
    apiSettings?.forEach(({ id, name, ipAddress }) =>
      apiSettingsOptions?.push({ name: `${name} ( ${ipAddress} )`, value: id })
    );
  }

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isMutation > 0 || isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        {apiState?.apiStatus && apiState?.apiMessage && (
          <MessageBox
            status={apiState?.apiStatus}
            message={apiState?.apiMessage}
            style={{ marginBottom: 20 }}
          />
        )}
        <StyledFiltersContainer className={showFilters ? "show" : ""}>
          <StyledFilters>
            <StyledFilterContainer>
              <Dropdown
                value={filters.fOperatorsId}
                onChange={(value) => handleFilterChange("fOperatorsId", value)}
                placeholder="Operator"
                disabled={isLoading}
                options={operatorsOptions}
              />
              <Dropdown
                value={filters.fCirclesId}
                onChange={(value) => handleFilterChange("fCirclesId", value)}
                placeholder="Circle"
                disabled={isLoading}
                options={circlesOptions}
              />
              <Dropdown
                value={filters.fCategoriesId}
                onChange={(value) => handleFilterChange("fCategoriesId", value)}
                placeholder="Category"
                disabled={isLoading}
                options={categoriesOptions}
              />
              <Dropdown
                value={filters.fRechargeStatus}
                onChange={(value) =>
                  handleFilterChange("fRechargeStatus", value)
                }
                placeholder="Status"
                disabled={isLoading}
                options={rechargeStatusOptions}
              />
              <Dropdown
                value={filters.fApiSettingsId}
                onChange={(value) =>
                  handleFilterChange("fApiSettingsId", value)
                }
                placeholder="API Setting"
                disabled={isLoading}
                options={apiSettingsOptions}
              />
              <TextInput
                value={filters.fRechargeNumber}
                onChange={(value) =>
                  handleFilterChange("fRechargeNumber", value)
                }
                placeholder="Recharge Number"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fFromDate}
                onChange={(value) => handleFilterChange("fFromDate", value)}
                placeholder="From Date"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fToDate}
                onChange={(value) => handleFilterChange("fToDate", value)}
                placeholder="To Date"
                disabled={isLoading}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: "Operator",
                  dataSelector: "operatorName",
                  dataType: "string",
                  cellrenderer: (value, item) => (
                    <StyledIconedText>
                      <Image
                        source={item.operatorsLogo || "/no-image.png"}
                        alt={value}
                        style={{ width: "50px", height: "50px" }}
                      />
                      {value}
                    </StyledIconedText>
                  ),
                },
                {
                  title: "Recharge Number",
                  dataSelector: "rechargeNumber",
                  dataType: "string",
                },
                {
                  title: "Amount",
                  dataSelector: "rechargeAmount",
                  dataType: "number",
                  cellrenderer: (value) => formatCurrency(value),
                },
                {
                  title: "Debit Amount",
                  dataSelector: "debitAmount",
                  dataType: "number",
                  cellrenderer: (value) => formatCurrency(value),
                },
                {
                  title: "Margin",
                  dataSelector: "buyersMargin",
                  dataType: "string",
                  textAlign: "center",
                  cellrenderer: (value) => `${value}%`,
                },
                {
                  title: "Status",
                  dataSelector: "rechargeStatus",
                  dataType: "string",
                  textAlign: "center",
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={
                        value === "Success"
                          ? "green"
                          : value === "Failed"
                            ? "red"
                            : "yellow"
                      }
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: "Operators Id",
                  dataSelector: "rechargeOperatorsId",
                  dataType: "string",
                  cellrenderer: (value) => value || "N/A",
                },
                {
                  title: "Category",
                  dataSelector: "category",
                  dataType: "string",
                },
                // {
                //   title: "Transaction Type",
                //   dataSelector: "transactionType",
                //   dataType: "string",
                // },
                {
                  title: "Circle",
                  dataSelector: "circleName",
                  dataType: "string",
                },
                {
                  title: "Transaction Time",
                  dataSelector: "createdOn",
                  dataType: "string",
                },
                {
                  title: "API Setting Name",
                  dataSelector: "apiSettingName",
                  dataType: "string",
                },
                {
                  title: "",
                  dataSelector: "id",
                  canSort: false,
                  isSticky: "Yes",
                  stickyAlign: "right",
                  isDummy: "Yes",
                  textAlign: "center",
                  width: "max-content",
                  cellrenderer: (value, item) => (
                    <StyledActionItemButtons>
                      {
                        item.rechargeStatus === "Success" &&
                        item.complaintOccurrenceCount < 3 &&
                        item.complaintResolutionStatus !== "Pending" && (
                      <CustomConfirmButton
                        showIcon={true}
                        showTitle={false}
                        isBuyerComplaint={true}
                        icon={faFlag}
                        title="Raise Complaint"
                        proceedButtonText="Confirm"
                        message="Are you sure, you want to raise complaint ?"
                        onClick={async (state) => {
                          await raiseComplaintMutation({
                            id: value,
                            ...state,
                          });
                        }}
                      />
                        )}
                      <CustomButton
                        icon={faEye}
                        onClick={() => toggleApiLogs(value)}
                        style={{ color: "orange" }}
                      />
                      {item.rechargeStatus === "Success" && (
                        <CustomButton
                          icon={faWarning}
                          onClick={() => {
                            setItem(item);
                            setModalType("WRONG_TO_RIGHT");
                            setShowModal(true);
                          }}
                          style={{ color: "orange" }}
                        />
                      )}
                    </StyledActionItemButtons>
                  ),
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
            <Modal
              modalStatus={showModal}
              setModalStatus={() => {
                setShowModal(false);
                setModalType("");
                setLogs([]);
                setItem(null);
                setReason("");
                setRemark("");
                setRightAccountNumber("");
              }}
              hideCloseButton
            >
              <StyledModalContainer
                width={modalType === "API_LOGS" ? `1200px` : `500px`}
              >
                <h3>
                  {modalType === "API_LOGS"
                    ? `API Call Logs`
                    : `Raise Wrong To Right Recharge`}
                </h3>
                {modalType === "API_LOGS" && (
                  <StyledApiCallLogsContainer>
                    {isLogsLoading && <LoadingSpinner />}
                    {!isLogsLoading && logs.length === 0 && (
                      <NoData message={`No logs found`} />
                    )}
                    {logs?.length > 0 &&
                      logs.map(
                        (
                          {
                            requestType,
                            request,
                            requestOn,
                            requestBody,
                            response,
                            responseOn,
                          },
                          index
                        ) => (
                          <Accordination
                            key={index}
                            header={`${requestType}`}
                            autoExpanded={true}
                          >
                            <Grid columns={"calc(50% - 10px) calc(50% - 10px)"}>
                              <GridItem>
                                <StyledBodyContainer>
                                  <StyledHeading>
                                    API Request{" "}
                                    {requestOn && `( ${requestOn} )`}
                                  </StyledHeading>
                                  <StyledContent>{request}</StyledContent>
                                </StyledBodyContainer>
                                {requestBody && (
                                  <StyledBodyContainer>
                                    <StyledHeading>
                                      API Request Body
                                    </StyledHeading>
                                    <StyledContent>{requestBody}</StyledContent>
                                  </StyledBodyContainer>
                                )}
                              </GridItem>
                              <GridItem>
                                <StyledBodyContainer>
                                  <StyledHeading>
                                    API Response{" "}
                                    {responseOn && `( ${responseOn} )`}
                                  </StyledHeading>
                                  <StyledContent>
                                    <JSONPretty
                                      id="json-pretty"
                                      data={response}
                                    ></JSONPretty>
                                  </StyledContent>
                                </StyledBodyContainer>
                              </GridItem>
                            </Grid>
                          </Accordination>
                        )
                      )}
                  </StyledApiCallLogsContainer>
                )}
                {modalType === "WRONG_TO_RIGHT" && (
                  <StyledFormContainer>
                    <Form method="POST" action="#" onSubmit={raiseWrongToRight}>
                      <Fieldset>Transaction ID : {item?.id}</Fieldset>
                      <Fieldset>
                        <Label required>Reason</Label>
                        <Dropdown
                          value={reason}
                          onChange={setReason}
                          placeholder="Select Reason"
                          options={[
                            {
                              name: "-- SELECT STATUS --",
                              value: "",
                            },
                            {
                              name: "Wrong Recharge Reverse",
                              value: "Wrong Recharge Reverse",
                            },
                            {
                              name: "Wrong Recharge to Right Recharge",
                              value: "Wrong Recharge to Right Recharge",
                            },
                          ]}
                          disabled={isLoadingUpdate}
                        />
                      </Fieldset>
                      <Fieldset>
                        <Label required>Right Account Number</Label>
                        <TextInput
                          value={rightAccountNumber}
                          onChange={setRightAccountNumber}
                          placeholder="Please enter right account number"
                          disabled={isLoadingUpdate}
                        />
                      </Fieldset>
                      <Fieldset>
                        <Label required>Remark</Label>
                        <TextInput
                          value={remark}
                          onChange={setRemark}
                          placeholder="Please enter remark"
                          disabled={isLoadingUpdate}
                        />
                      </Fieldset>
                      {showModal && responseStatus !== "" && (
                        <Fieldset>
                          <MessageBox
                            status={responseStatus}
                            message={responseMessage}
                          />
                        </Fieldset>
                      )}
                      <Fieldset>
                        <Button
                          disabled={isLoadingUpdate}
                          type="submit"
                          style={{ width: "auto" }}
                        >
                          Submit
                        </Button>
                      </Fieldset>
                      <Loader isLoading={isLoadingUpdate} />
                    </Form>
                  </StyledFormContainer>
                )}
              </StyledModalContainer>
            </Modal>
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(BuyerTransactionsList);
